import React, { useEffect, useMemo, useState } from "react";
import FontIcon from "react-md/lib/FontIcons";
import moment from "moment";
import EvaluationModal from "./EvaluationModal";
import {
  renderAIBaselineColumn,
  renderAuditEvalColumn,
} from "./EvaluationColumns";
import {
  AUDIT_EVENT_SUMMARY_UPDATED,
  EVALUATION_STATUS_CORRECT,
  EVALUATION_STATUS_INCORRECT,
} from "../../utils/riskUtils";
// The comparison body reuses the risk detail page's eval-compare styles.
import "../../styles/riskDetail.scss";

const VERDICT_LABEL = {
  [EVALUATION_STATUS_CORRECT]: "Correct",
  [EVALUATION_STATUS_INCORRECT]: "Incorrect",
};

const fmt = (d) => (d ? moment(d).format("DD MMM YYYY, hh:mm A") : "-");

/**
 * Side-by-side evaluation history for one risk record. Each saved evaluation is
 * a version; version N is compared with version N-1, and version 1 with the
 * original AI output.
 *
 * audits are the risk record's audit rows (any event type — non-evaluation
 * rows are ignored). initialAuditId picks the version to open on; it defaults
 * to the latest.
 */
function EvaluationSummaryDialog({
  open,
  record,
  audits,
  initialAuditId,
  patientId,
  onClose,
}) {
  const versions = useMemo(
    () =>
      (audits || [])
        .filter((a) => a.eventType === AUDIT_EVENT_SUMMARY_UPDATED)
        .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)),
    [audits],
  );

  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!open) return;
    const at = versions.findIndex((v) => v.id === initialAuditId);
    setIndex(at >= 0 ? at : Math.max(versions.length - 1, 0));
  }, [open, initialAuditId, versions]);

  if (!open || !record) return null;

  const current = versions[index];
  const previous = index > 0 ? versions[index - 1] : null;
  const isLatest = index === versions.length - 1;
  const verdict = VERDICT_LABEL[current?.metadata?.evaluationStatus];
  const pid = patientId || record.patientId;

  return (
    <EvaluationModal open={open} onClose={onClose} className="wide">
      <div className="ai-eval-header">
        <div>
          <h3 className="ai-eval-title">Evaluation Summary</h3>
          {pid && <p className="ai-eval-header-sub">PID-{pid}</p>}
        </div>
        <button
          type="button"
          className="ai-eval-close"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      {versions.length === 0 ? (
        <div className="ai-eval-body">
          <p className="eval-compare-empty">
            This AI summary has not been evaluated yet.
          </p>
        </div>
      ) : (
        <>
          <div className="ai-eval-version-bar">
            <span className="ai-eval-version-label">Viewing version</span>
            <button
              type="button"
              className="ai-eval-version-nav"
              disabled={index === 0}
              onClick={() => setIndex(index - 1)}
              aria-label="Previous version"
            >
              <FontIcon>chevron_left</FontIcon>
            </button>
            <div className="ai-eval-version-tabs" role="tablist">
              {versions.map((v, i) => (
                <button
                  key={v.id || i}
                  type="button"
                  role="tab"
                  aria-selected={i === index}
                  className={`ai-eval-version-tab${i === index ? " selected" : ""}`}
                  onClick={() => setIndex(i)}
                >
                  Version {i + 1}
                  {i === versions.length - 1 && (
                    <span className="ai-eval-latest-tag">Latest</span>
                  )}
                </button>
              ))}
            </div>
            <button
              type="button"
              className="ai-eval-version-nav"
              disabled={isLatest}
              onClick={() => setIndex(index + 1)}
              aria-label="Next version"
            >
              <FontIcon>chevron_right</FontIcon>
            </button>
            <span className="ai-eval-version-meta">
              Version {index + 1} of {versions.length} · evaluated{" "}
              {fmt(current.createdAt)} by {current.actorName || "-"}
              {verdict ? ` · marked ${verdict}` : ""}
            </span>
          </div>

          <div className="eval-compare-body">
            <div className="eval-compare-col">
              <div
                className={`eval-compare-col-header ${previous ? "prev" : "ai"}`}
              >
                <span className="eval-compare-col-label">
                  {previous ? "Previous evaluation" : "AI assessment"}
                </span>
                <div className="eval-compare-col-name">
                  {previous ? previous.actorName || "-" : "AI System"}
                </div>
                <div className="eval-compare-col-date">
                  {previous
                    ? fmt(previous.createdAt)
                    : fmt(record.completedAt || record.triggeredAt)}
                </div>
              </div>
              {previous
                ? renderAuditEvalColumn(previous.metadata)
                : renderAIBaselineColumn(record, { driverTags: true })}
            </div>
            <div className="eval-compare-col">
              <div className="eval-compare-col-header latest">
                <span className="eval-compare-col-label">
                  {isLatest ? "Latest evaluation" : `Evaluation v${index + 1}`}
                </span>
                <div className="eval-compare-col-name">
                  {current.actorName || "-"}
                </div>
                <div className="eval-compare-col-date">
                  {fmt(current.createdAt)}
                </div>
              </div>
              {renderAuditEvalColumn(current.metadata)}
            </div>
          </div>
        </>
      )}
    </EvaluationModal>
  );
}

export default EvaluationSummaryDialog;
